import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import PermissionGuard from './components/PermissionGuard';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import SaisiePage from './pages/SaisiePage';
import ListeSaisiesPage from './pages/ListeSaisiesPage';
import ModifierSaisiePage from './pages/ModifierSaisiePage';
import ImportPage from './pages/ImportPage';
import FacturationPage from './pages/FacturationPage';
import ListeFacturationsPage from './pages/ListeFacturationsPage';
import RapportsPage from './pages/RapportsPage';
import CaissePage from './pages/CaissePage';
import ModifierFacturationPage from './pages/ModifierFacturationPage';
import GestionUtilisateursPage from './pages/GestionUtilisateursPage';
import GestionRolesPage from './pages/GestionRolesPage';
import AuditLogPage from './pages/AuditLogPage';
import ChangerMotDePassePage from './pages/ChangerMotDePassePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ParametresPage from './pages/Parametres';
import AchatsPage from './pages/AchatsPage';
import DossierDetailPage from './pages/DossierDetailPage'
import SuiviExecutionPage from './pages/SuiviExecutionPage';

const RouteProtegee = ({ children }) => {
  const { utilisateur } = useAuth();
  if (!utilisateur) return <Navigate to="/login" />;
  if (utilisateur.premier_login && window.location.pathname !== '/changer-mot-de-passe') {
    return <Navigate to="/changer-mot-de-passe" />;
  }
  return children;
};

// Layout avec Sidebar — appliqué aux modules principaux
const AvecSidebar = ({ children }) => {
  const { utilisateur } = useAuth();
  if (!utilisateur) return <Navigate to="/login" />;
  if (utilisateur.premier_login && window.location.pathname !== '/changer-mot-de-passe') {
    return <Navigate to="/changer-mot-de-passe" />;
  }
  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <Sidebar />
      <div style={{ flex: 1, background: '#F4F6F9', minHeight: '100vh', overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Toujours accessible après connexion */}
          <Route path="/changer-mot-de-passe" element={<RouteProtegee><ChangerMotDePassePage /></RouteProtegee>} />
          <Route path="/dashboard" element={<RouteProtegee><Dashboard /></RouteProtegee>} />

          {/* Module 1 — Production / Abattage */}
          <Route path="/saisie" element={
            <AvecSidebar>
              <PermissionGuard module="abattage" action="lire">
                <SaisiePage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/saisies" element={
            <AvecSidebar>
              <PermissionGuard module="abattage" action="lire">
                <ListeSaisiesPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/saisies/:id/modifier" element={
            <AvecSidebar>
              <PermissionGuard module="abattage" action="ecrire">
                <ModifierSaisiePage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/import" element={
            <AvecSidebar>
              <PermissionGuard module="abattage" action="ecrire">
                <ImportPage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Module 2 — Facturation */}
          <Route path="/facturation/nouvelle" element={
            <AvecSidebar>
              <PermissionGuard module="facturation" action="ecrire">
                <FacturationPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/facturation/liste" element={
            <AvecSidebar>
              <PermissionGuard module="facturation" action="lire">
                <ListeFacturationsPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/facturation/:id/modifier" element={
            <AvecSidebar>
              <PermissionGuard module="facturation" action="ecrire">
                <ModifierFacturationPage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Module 3 — Caisse */}
          <Route path="/caisses" element={
            <AvecSidebar>
              <PermissionGuard module="caisse" action="lire">
                <CaissePage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Module 4 — Rapports */}
          <Route path="/rapports" element={
            <AvecSidebar>
              <PermissionGuard module="rapports" action="lire">
                <RapportsPage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Module 5 — Achats & Décaissements */}
          <Route path="/achats" element={
            <AvecSidebar>
              <PermissionGuard module="achats" action="lire">
                <AchatsPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/achats/dossiers/:numero/suivi" element={<AvecSidebar><SuiviExecutionPage /></AvecSidebar>} />
          <Route path="/achats/dossiers/:numero" element={
            <AvecSidebar>
              <PermissionGuard module="achats" action="lire">
                <DossierDetailPage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Administration */}
          <Route path="/users" element={
            <AvecSidebar>
              <PermissionGuard module="utilisateurs" action="lire">
                <GestionUtilisateursPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/users/audit" element={
            <AvecSidebar>
              <PermissionGuard module="utilisateurs" action="lire">
                <AuditLogPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/parametres" element={
            <AvecSidebar>
              <PermissionGuard module="parametres" action="lire">
                <ParametresPage />
              </PermissionGuard>
            </AvecSidebar>
          } />
          <Route path="/roles" element={
            <AvecSidebar>
              <PermissionGuard module="utilisateurs" action="supprimer">
                <GestionRolesPage />
              </PermissionGuard>
            </AvecSidebar>
          } />

          {/* Redirections */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
